import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { apiGet } from "../../utils/api";
import { hasPermission } from "../../utils/permission";
import { useApp } from "../../design/AppContext";
import { DataRow, EmptyState, StatusChip, UserAvatar } from "../../components/mui";
import { MdChevronLeft, MdChevronRight, MdLockOutline } from "react-icons/md";

/**
 * 團隊績效 — 1:1 對應 DESIGN 稿 L327-L344（markup）與 L1159-L1160（teamKpis / ranking）。
 *
 * 設計稿的資料是寫死的種子；這裡改由 GET_PerformanceTeam 取當月團隊報表，
 * 再用 /Auth/Admin/List 補上排行列的「角色」文字（團隊 API 只回帳號與姓名）。
 */

/** 後端數值可能是字串或小數，統一轉成設計稿要顯示的整數 */
const num = (v) => Math.round(Number(v) || 0);

function App(props) {
  const { team, GET_PerformanceTeam, POST_PerformanceSnapshot } = props;
  const { toast, confirmDelete } = useApp();

  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  /** 帳號 → 主要角色名稱（排行列的裝飾文字） */
  const [roleOf, setRoleOf] = useState({});
  const [snapping, setSnapping] = useState(false);

  const canSnapshot = hasPermission("performance.create");


  useEffect(() => {
    GET_PerformanceTeam({ year, month });
  }, [GET_PerformanceTeam, year, month]);

  // 角色文字只跟人員有關、與月份無關，所以獨立抓一次即可。
  // /Auth/Admin/List 是 [PermissionOnly("admin")]，但本頁權限只需 dashboard；
  // 走 saga 的話非管理員會 403 → utils/exception 跳原生 alert，因此直接呼叫並吞掉錯誤，
  // 取不到就讓角色文字留白（純裝飾，不影響排行本身）。
  useEffect(() => {
    let cancelled = false;
    apiGet("/Auth/Admin/List")
      .then((list) => {
        if (cancelled) return;
        const map = {};
        (Array.isArray(list) ? list : []).forEach((a) => {
          const account = _.get(a, "account", "");
          if (!account) return;
          map[account] = _.get(a, "role.name", "") || _.get(a, "roles[0].name", "") || "";
        });
        setRoleOf(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /** 用 Date 位移避免自己處理跨年進位 */
  const shiftMonth = (delta) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const members = _.get(team, "members", []) || [];

  // 設計稿的「已鎖定 · 月結完成」語意是月結已產生快照；
  // 後端 is_locked 只代表過了鎖定日，仍須確認確實有 snapshot 才顯示。
  const locked =
    Boolean(_.get(team, "is_locked", false)) && members.some((m) => _.get(m, "is_snapshot", false));

  /**
   * 產生月結快照 —— 把這個月的績效算完存起來，之後補登的工時不會再改動它。
   *
   * 用 confirmDelete 借它的確認對話框（不是刪除，所以按鈕字樣自己指定）：
   * 重新產生會覆蓋掉已經鎖定的分數，那是會影響考績的事，值得再問一次。
   */
  const makeSnapshot = () => {
    if (snapping) return;
    const run = () =>
      POST_PerformanceSnapshot(
        { year, month },
        () => { toast(`已產生 ${year} 年 ${month} 月的月結快照`); GET_PerformanceTeam({ year, month }); },
        setSnapping
      );

    if (!locked) { run(); return; }
    confirmDelete({
      title: "重新產生月結快照？",
      subtitle: "將覆蓋已鎖定的分數",
      body: `${year} 年 ${month} 月已完成結算。重新產生將以目前的資料覆蓋當時鎖定的分數；若期間有補登工時或修改工項，分數將隨之變動。`,
      confirmText: "重新產生",
      onConfirm: run,
    });
  };

  /** 團隊平均（成員為空時回 0，避免 NaN 進到 width） */
  const avgOf = (pick) =>
    members.length
      ? Math.round(members.reduce((s, m) => s + (Number(pick(m)) || 0), 0) / members.length)
      : 0;

  const teamKpis = [
    { label: "平均完成率", val: `${avgOf((m) => _.get(m, "kpi.completion_rate", 0))}%` },
    { label: "平均準時率", val: `${avgOf((m) => _.get(m, "kpi.on_time_rate", 0))}%` },
    { label: "平均紮實度", val: `${avgOf((m) => _.get(m, "kpi.solidity_score", 0))}%` },
    {
      // 後端只回原始工項數，工作量指數＝min(100, 工項數×5)，與 overall_score 的權重算法一致
      label: "工作量指數",
      val: `${avgOf((m) => Math.min(100, (Number(_.get(m, "kpi.workload", 0)) || 0) * 5))}%`,
    },
  ];

  const ranking = [...members]
    .sort((a, b) => (Number(_.get(b, "overall_score", 0)) || 0) - (Number(_.get(a, "overall_score", 0)) || 0))
    .map((m, i) => {
      const name = _.get(m, "name", "") || _.get(m, "account", "");
      return {
        key: _.get(m, "account", "") || `${name}-${i}`,
        rank: i + 1,
        name,
        role: roleOf[_.get(m, "account", "")] || "",
        // 前三名用金銀銅的固定色，其餘是淺底
        rankBg: i === 0 ? "#0e7c66" : i === 1 ? "#2563eb" : i === 2 ? "#dc6803" : "action.hover",
        done: `${num(_.get(m, "kpi.completion_rate", 0))}%`,
        score: num(_.get(m, "overall_score", 0)),
      };
    });

  return (
    <Box className="pg" sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Tooltip title="上個月">
            <IconButton aria-label="上個月" onClick={() => shiftMonth(-1)}>
              <MdChevronLeft aria-hidden="true" />
            </IconButton>
          </Tooltip>
          <Typography variant="h3" component="div">{`${year} 年 ${month} 月`}</Typography>
          <Tooltip title="下個月">
            <IconButton aria-label="下個月" onClick={() => shiftMonth(1)}>
              <MdChevronRight aria-hidden="true" />
            </IconButton>
          </Tooltip>
        </Stack>
        {locked && <StatusChip kind="warn" mark={<MdLockOutline />}>已鎖定 · 月結完成</StatusChip>}

        <Box sx={{ flex: 1 }} />

        {/*
          產生月結快照。原本這支 API 沒有任何入口，也沒有排程會叫它 ——
          於是 performance_snapshot 永遠是空的、「已鎖定 · 月結完成」不可能亮，
          而導覽早就對使用者承諾了「次月 6 日之後該月會鎖定」。
        */}
        {canSnapshot && (
          <Button
            variant="outlined"
            disabled={snapping}
            loading={snapping}
            loadingPosition="start"
            onClick={makeSnapshot}
            title="鎖定本月的績效結果，之後補登的工時不會再影響已鎖定的分數"
            startIcon={<MdLockOutline />}
          >
            {snapping ? "產生中…" : locked ? "重新產生月結快照" : "產生月結快照"}
          </Button>
        )}
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 1.75 }}>
        {teamKpis.map((k) => (
          <Card key={k.label}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {k.label}
              </Typography>
              <Typography variant="h1" component="div" sx={{ fontWeight: 900, my: 1 }}>
                {k.val}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, parseFloat(k.val) || 0))}
                aria-label={k.label}
                sx={{ bgcolor: "action.hover" }}
              />
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card sx={{ overflow: "hidden" }}>
        <CardHeader title="成員績效排行" slotProps={{ title: { variant: "h3", component: "h2" } }} sx={{ borderBottom: 1, borderColor: "divider" }} />
        {ranking.map((r) => (
          <DataRow key={r.key}>
            <Box
              component="span"
              sx={{
                width: 26,
                height: 26,
                borderRadius: "var(--radius-sm)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "var(--t-meta)",
                fontWeight: 900,
                flexShrink: 0,
                color: r.rank <= 3 ? "#fff" : "text.secondary",
                bgcolor: r.rankBg,
              }}
            >
              {r.rank}
            </Box>
            <UserAvatar name={r.name} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>{r.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {r.role}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2.25} sx={{ alignItems: "center" }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="body2" color="text.secondary">
                  完成率
                </Typography>
                <Typography sx={{ fontWeight: 800 }}>{r.done}</Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="body2" color="text.secondary">
                  綜合
                </Typography>
                <Typography variant="h3" component="div" color="primary" sx={{ fontWeight: 900 }}>
                  {r.score}
                </Typography>
              </Box>
            </Stack>
          </DataRow>
        ))}
        {ranking.length === 0 && <EmptyState dense text="此月份無績效資料" />}
      </Card>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  team: _.get(state, "Performance.Team", {}),
});

const mapDispatchToProps = (dispatch) => ({
  GET_PerformanceTeam(payload, callback, loading) {
    dispatch({ type: "GET_PerformanceTeam", payload, callback, loading });
  },
  POST_PerformanceSnapshot(payload, callback, loading) {
    dispatch({ type: "POST_PerformanceSnapshot", payload, callback, loading });
  },
});

App.propTypes = {
  team: PropTypes.object,
  GET_PerformanceTeam: PropTypes.func,
  POST_PerformanceSnapshot: PropTypes.func,
};

App.defaultProps = {
  team: {},
  GET_PerformanceTeam: () => {},
  POST_PerformanceSnapshot: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
